import { Check, CreditCard, Search } from "lucide-react";

import { Initials } from "@/features/platform/components/app-screens";
import { CityMap } from "@/features/platform/components/city-map";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

/**
 * The pictures on `/features` that are not a screen: the passes a business sells, the map clients
 * find it on, and the watch face we are building. Sample people and numbers.
 */

/** Two passes fanned like cards in a wallet, and a payment landing on top of them. */
export const PassesArt = () => (
  <div aria-hidden className="absolute inset-0 grid place-items-center [perspective:1200px]">
    <div className="relative h-[230px] w-[min(340px,78%)]">
      <div className="absolute inset-0 translate-x-[9%] -translate-y-[16%] rotate-[8deg] rounded-[26px] bg-[linear-gradient(140deg,oklch(0.32_0.02_240),oklch(0.2_0.01_240))] p-6 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.8)] ring-1 ring-white/10 ring-inset">
        <p className="text-[13px] font-semibold tracking-[0.12em] text-white/55 uppercase">{m.features_pass_monthly()}</p>
        <p className="mt-2 text-[26px] font-semibold tracking-[-0.02em] text-white">{m.features_money_monthly()}</p>
      </div>
      <div className="absolute inset-0 -rotate-[4deg] overflow-hidden rounded-[26px] bg-[linear-gradient(140deg,color-mix(in_oklch,var(--primary)_92%,white),var(--primary)_45%,color-mix(in_oklch,var(--primary)_70%,black))] p-6 text-primary-foreground shadow-[0_40px_70px_-20px_rgba(0,0,0,0.85)] [transform:rotateX(8deg)_rotate(-4deg)]">
        <span className="absolute -top-16 -right-16 size-48 rounded-full bg-white/20 blur-2xl" />
        <div className="relative flex items-start justify-between">
          <p className="text-[13px] font-semibold tracking-[0.12em] uppercase opacity-70">{m.features_pack_name()}</p>
          <CreditCard className="size-6 opacity-70" />
        </div>
        <p className="relative mt-3 text-[34px] leading-none font-bold tracking-[-0.03em] tabular-nums">
          {m.features_pack_left({ left: 6, total: 10 })}
        </p>
        <span className="relative mt-5 grid grid-cols-10 gap-1">
          {Array.from({ length: 10 }, (_, index) => (
            <i key={index} className={cn("h-2 rounded-full", index < 4 ? "bg-primary-foreground/20" : "bg-primary-foreground")} />
          ))}
        </span>
        <p className="relative mt-5 text-[14px] font-semibold opacity-75">{m.features_sample_client_full()}</p>
      </div>
      <div className="absolute -bottom-[22%] left-1/2 flex w-[min(320px,104%)] -translate-x-1/2 items-center gap-3 rounded-[22px] bg-[oklch(0.2_0.006_240/0.8)] p-3.5 pr-4 text-white shadow-[0_24px_50px_-14px_rgba(0,0,0,0.8)] ring-1 ring-white/10 backdrop-blur-xl ring-inset">
        <span className="grid size-10 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground">
          <Check className="size-5" strokeWidth={3} />
        </span>
        <span className="min-w-0 flex-1 leading-tight">
          <b className="block truncate text-[15px] font-semibold">{m.features_paid({ name: m.features_sample_client_first() })}</b>
          <small className="text-[13px] text-white/55">{m.features_paid_detail()}</small>
        </span>
        <b className="text-[16px] font-semibold tabular-nums">{m.features_money_session()}</b>
      </div>
    </div>
  </div>
);

const DOTS = ["left-[16%] top-[24%]", "left-[30%] top-[62%]", "left-[74%] top-[20%]", "left-[86%] top-[52%]", "left-[60%] top-[72%]"];

/** A night map with the business pinned on it, and the directory row a client nearby sees. */
export const DirectoryMap = () => (
  <div aria-hidden className="absolute inset-0">
    <CityMap className="absolute inset-0" />
    <span className="absolute inset-0 bg-[radial-gradient(120%_80%_at_50%_0%,transparent_40%,oklch(0.13_0.006_240)_100%)]" />
    {DOTS.map((spot) => (
      <span key={spot} className={cn("absolute size-3 rounded-full bg-foreground/35 ring-4 ring-foreground/10", spot)} />
    ))}
    <span className="absolute top-[30%] left-[50%] -translate-x-1/2 -translate-y-1/2">
      <span className="relative grid size-14 place-items-center">
        <span className="absolute inset-0 animate-ping rounded-full bg-primary/40 motion-reduce:hidden" />
        <Initials
          name={m.features_sample_coach_full()}
          className="relative size-14 rounded-full bg-primary from-primary to-primary text-[17px] text-primary-foreground ring-4 ring-[oklch(0.13_0.006_240)]"
        />
      </span>
    </span>
    <div className="absolute inset-x-4 bottom-4 rounded-[24px] bg-[oklch(0.2_0.006_240/0.8)] p-2 shadow-[0_24px_60px_-16px_rgba(0,0,0,0.7)] ring-1 ring-white/10 backdrop-blur-xl ring-inset md:inset-x-auto md:left-6 md:w-[380px]">
      <div className="flex h-11 items-center gap-2.5 rounded-[16px] bg-foreground/[0.06] px-3.5 text-[15px] text-muted-foreground">
        <Search className="size-4" />
        {m.features_directory_search()}
      </div>
      <div className="mt-1 flex items-center gap-3 rounded-[16px] bg-foreground/[0.06] px-2.5 py-2.5">
        <Initials name={m.features_sample_coach_full()} className="size-11 rounded-[14px] text-[14px]" />
        <span className="min-w-0 flex-1 leading-tight">
          <b className="block truncate text-[15px] font-semibold">{m.features_sample_coach_full()}</b>
          <small className="block truncate text-[13px] text-muted-foreground">{m.features_directory_detail()}</small>
        </span>
      </div>
      <div className="flex gap-1.5 px-2.5 pt-2.5 pb-1.5">
        {["17:30", "18:30", "19:30"].map((time, index) => (
          <span
            key={time}
            className={cn(
              "rounded-full px-3 py-1.5 text-[13px] font-semibold tabular-nums",
              index === 0 ? "bg-primary text-primary-foreground" : "bg-foreground/[0.08]",
            )}
          >
            {time}
          </span>
        ))}
      </div>
    </div>
  </div>
);

const RING = 2 * Math.PI * 70;

/** The watch face we are building: who comes next, and how long until they do. 208 × 258 pt. */
export const NextClientFace = () => (
  <div className="absolute inset-0 flex flex-col items-center px-4 pt-4 pb-5">
    <div className="flex w-full items-center justify-between px-2 text-[15px] font-semibold">
      <span className="text-primary">{m.features_watch_next_label()}</span>
      <span className="tabular-nums">18:05</span>
    </div>
    <div className="relative mt-2 grid size-[150px] place-items-center">
      <svg viewBox="0 0 160 160" className="absolute inset-0 -rotate-90">
        <circle cx="80" cy="80" r="70" className="fill-none stroke-white/15" strokeWidth="11" />
        <circle
          cx="80"
          cy="80"
          r="70"
          className="fill-none stroke-primary"
          strokeWidth="11"
          strokeLinecap="round"
          strokeDasharray={RING}
          strokeDashoffset={RING * 0.58}
        />
      </svg>
      <span className="text-center leading-none">
        <b className="block text-[40px] font-semibold tracking-[-0.03em] tabular-nums">18:30</b>
        <small className="mt-1 block text-[13px] text-white/55">{m.features_today_in({ minutes: 25 })}</small>
      </span>
    </div>
    <p className="mt-auto text-center leading-tight">
      <b className="block text-[16px] font-semibold">{m.features_sample_client_full()}</b>
      <small className="text-[13px] text-white/55">{m.features_sample_session()}</small>
    </p>
  </div>
);
